import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useChat, type UseChatHelpers } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { HttpStatusCode } from '@bridebook/toolbox/src/api/http-status';
import { chatTransportFetch } from '@bridebook/web/lib/ai-assistant/chat-transport-fetch';
import { AI_SEARCH_VERSION, DEFAULT_MODEL_ID } from '@bridebook/web/lib/ai-assistant/config';
import { useAIPromptContext } from '@bridebook/web/lib/ai-assistant/hooks/use-ai-prompt-context';
import { useRateLimitExpire } from '@bridebook/web/lib/ai-assistant/hooks/use-rate-limit-clock';
import { parseRateLimitFromResponse } from '@bridebook/web/lib/ai-assistant/parse-rate-limit';
import { classifyResultMessage } from '@bridebook/web/lib/ai-assistant/result-classification';
import { findLatestSearchOutput } from '@bridebook/web/lib/ai-assistant/search-output';
import {
  clearAIAssistantConversation,
  setAILastSearch,
} from '@bridebook/web/lib/ai-assistant/slice';
import { buildAnalyticsActiveFilters } from '@bridebook/web/lib/search/analytics-builders/build-active-filters';
import type { AISearchUIMessage } from '@bridebook/web/lib/ai-assistant/tools';
import type {
  AIRateLimit,
  AISearchBody,
  AISearchSearchResult,
  AISearchStatusStage,
  PendingPromptMetadata,
} from '@bridebook/web/lib/ai-assistant/types';
import { aiPromptSentAnalytics, type IAIPromptSentPayload } from '@bridebook/web/lib/ui/analytics';
import { useDispatch } from '@bridebook/web/lib/utils';

type Helpers = UseChatHelpers<AISearchUIMessage>;

type AIAssistantContextValue = {
  messages: Helpers['messages'];
  sendMessage: Helpers['sendMessage'];
  setMessages: Helpers['setMessages'];
  error: Helpers['error'];
  stop: Helpers['stop'];
  status: Helpers['status'];
  liveStage: AISearchStatusStage | null;
  promptContextReady: boolean;
  contextTrimmed: boolean;
  /** Set when the conversation is too long to send even after trimming history. */
  conversationTooLong: boolean;
  rateLimit: AIRateLimit | null;
  /** Re-send a not-sent message's text as a fresh turn and drop the old bubble. */
  retryMessage: (message: AISearchUIMessage) => void;
  /** Discard a not-sent message: remove it from the conversation and the failed set. */
  dismissMessage: (id: string) => void;
  clearConversation: () => void;
  /** Read a carousel's saved horizontal scroll offset, keyed by message id. */
  getCarouselScroll: (key: string) => number | undefined;
  /** Save a carousel's horizontal scroll offset, keyed by message id. */
  setCarouselScroll: (key: string, offset: number) => void;
};

const AIAssistantCtx = createContext<AIAssistantContextValue | null>(null);

// Drop messages a rate limit rejected (flagged with `metadata.rateLimited`) from
// the outgoing payload. They stay visible in the chat with a "Not sent" badge,
// but replaying an orphaned user turn (no assistant reply) as history would
// confuse the model on every later request.
const stripRateLimitedMessages = (
  body: BodyInit | null | undefined,
): BodyInit | null | undefined => {
  if (typeof body !== 'string') return body;
  const parsedBody = JSON.parse(body) as AISearchBody;
  const messages = parsedBody.messages.filter((m) => !m.metadata?.rateLimited);
  if (messages.length === parsedBody.messages.length) return body;
  return JSON.stringify({ ...parsedBody, messages });
};

const extractText = (message: AISearchUIMessage | null | undefined): string => {
  if (!message) return '';
  const chunks: string[] = [];
  for (const part of message.parts) {
    if (part.type === 'text' && part.text) chunks.push(part.text);
  }
  return chunks.join('');
};

/**
 * Assembles the `AI Prompt Sent` BigQuery payload. `message` is the finished
 * assistant turn, or `null` for a turn that failed before any reply (e.g. the
 * over-length gate) — in that case pass `statusOverride` to record why, so BQ
 * still receives the attempt instead of losing it.
 */
const buildAiPromptSentPayload = (
  message: AISearchUIMessage | null,
  messages: AISearchUIMessage[],
  toolOutput: AISearchSearchResult | null,
  statusOverride?: IAIPromptSentPayload['resultsMessageStatus'],
): IAIPromptSentPayload => {
  const userMessages = messages.filter((m) => m.role === 'user');
  const parent = userMessages[0];
  const child = userMessages[userMessages.length - 1];
  const metadata: PendingPromptMetadata = (child?.metadata as
    | PendingPromptMetadata
    | undefined) ?? {
    promptSource: 'typed',
  };
  const candidates = toolOutput?.candidates ?? [];
  // A non-venue request never runs a search, so `supplierSearch` is sourced from
  // the `declineOutOfScope` tool signal rather than the result count.
  const declinedNonVenue =
    message?.parts.some(
      (p) =>
        p.type === 'tool-declineOutOfScope' &&
        p.state === 'output-available' &&
        p.output.reason === 'nonVenue',
    ) ?? false;
  const resultsMessageStatus =
    statusOverride ??
    (declinedNonVenue ? 'supplierSearch' : toolOutput ? classifyResultMessage(toolOutput) : null);

  const recommendedFields =
    metadata.promptSource === 'recommended'
      ? {
          // Initial pills carry a stable template id; LLM refinement chips don't.
          // Tag the latter `iterative` so BQ can tell follow-up recommended
          // prompts apart from initial ones (previously tracked as null).
          recommendedPromptTemplate: metadata.recommendedPromptTemplate ?? 'iterative',
          recommendedPromptOrder: metadata.recommendedPromptOrder,
          recommendedPromptOptions: metadata.recommendedPromptOptions,
          searchActiveFiltersSource: metadata.searchActiveFiltersSource,
        }
      : {};

  return {
    version: AI_SEARCH_VERSION,
    parentPromptId: parent?.id ?? null,
    childPromptId: child?.id ?? null,
    promptContent: extractText(child),
    promptSource: metadata.promptSource,
    ...recommendedFields,
    // Null on conversational/non-search turns so empty filters and a 0 count
    // don't skew the venue-search figures. When a search ran, run its filters
    // through the same builder the `Loaded search results` event uses so the
    // two events' `searchActiveFilters` are directly comparable (priceBracket
    // array, capacity/accommodation/lateAvailability folded into misc).
    searchActiveFilters: toolOutput
      ? buildAnalyticsActiveFilters(toolOutput.filters, toolOutput.fields)
      : null,
    carouselFlag: candidates.length > 0,
    carouselLoadedList: candidates.map((c) => c.id),
    resultsCount: toolOutput ? toolOutput.totalResults : null,
    ...(resultsMessageStatus ? { resultsMessageStatus } : {}),
    searchAiResponseContent: extractText(message),
  };
};

const trimContextBody = (body: BodyInit | null | undefined, offset: number) => {
  if (offset === 0 || typeof body !== 'string') return body;
  const parsedBody = JSON.parse(body) as AISearchBody;

  // Keep the first [user, assistant] pair (the original intent of the
  // conversation) and drop pairs from the middle instead of the front.
  return JSON.stringify({
    ...parsedBody,
    messages: [...parsedBody.messages.slice(0, 2), ...parsedBody.messages.slice(2 + offset)],
  });
};

export const AIAssistantProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();

  // `promptContextRef` is held inside the hook so transport reads always pick up
  // the latest page state (search params + couple profile) without
  // re-rendering this Provider when those upstream selectors change.
  const { promptContextRef, promptContextReady } = useAIPromptContext({ isSearchPageOpened: true });

  const [rateLimit, setRateLimit] = useState<AIRateLimit | null>(null);

  // Stable per-conversation id used as Langfuse sessionId so multi-turn
  // messages group into one Sessions view entry. Regenerated by
  // `clearConversation` when the user starts a new conversation. Not persisted
  // across reloads — each fresh page load starts a new session, which is the
  // right granularity for an in-page assistant.
  const chatIdRef = useRef<string>(crypto.randomUUID());

  // Tracks how many messages to skip from the start when sending to the server.
  // Incremented on each over-length rejection. Never modifies the UI messages
  // array - the full history stays visible in the chat.
  const contextStartRef = useRef(0);

  // Set by the transport when the server rejects the request as too large (413,
  // the `sanitize-messages` gate). Read once in `onError` to drive the same
  // trim-and-retry path as the model's in-stream `context_length_exceeded`.
  const overLengthRef = useRef(false);

  // Set by the transport on a 429. Read once in `onError` to emit the BQ event
  // (tagged `rateLimitReached`) so the throttled attempt isn't lost.
  const rateLimitedRef = useRef(false);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<AISearchUIMessage>({
        api: '/api/ai-assistant',
        body: () => ({
          ...promptContextRef.current,
          modelId: DEFAULT_MODEL_ID,
          chatId: chatIdRef.current,
        }),
        fetch: async (input, init) => {
          const response = await chatTransportFetch(input, {
            ...init,
            body: trimContextBody(stripRateLimitedMessages(init?.body), contextStartRef.current),
          });

          if (response.status === 429) {
            rateLimitedRef.current = true;
            const parsed = await parseRateLimitFromResponse(response);
            if (parsed) {
              setRateLimit(parsed);
            }
          }
          if (response.status === HttpStatusCode.PayloadTooLarge) {
            overLengthRef.current = true;
          }
          return response;
        },
      }),
    [promptContextRef],
  );

  const [liveStage, setLiveStage] = useState<AISearchStatusStage | null>(null);
  const [contextTrimmed, setContextTrimmed] = useState(false);
  const [conversationTooLong, setConversationTooLong] = useState(false);
  // Owned display error - set only when we decide to surface an error to the UI.
  // Kept separate from the SDK's internal error so context-limit retries don't
  // cause a banner flash (the SDK sets its error before onError fires).
  const [displayError, setDisplayError] = useState<Helpers['error']>(undefined);

  const carouselScrollPositions = useRef(new Map<string, number>());
  const getCarouselScroll = useCallback(
    (key: string) => carouselScrollPositions.current.get(key),
    [],
  );
  const setCarouselScroll = useCallback((key: string, offset: number) => {
    carouselScrollPositions.current.set(key, offset);
  }, []);

  const { messages, sendMessage, setMessages, stop, status, regenerate, clearError } =
    useChat<AISearchUIMessage>({
      transport,
      onData: (dataPart) => {
        if (dataPart.type === 'data-status') {
          setLiveStage(dataPart.data.stage);
        }
      },
      // `onFinish` fires on any stream end (normal, abort or error). We mirror the
      // search result and emit the BQ event only for clean completions.
      onFinish: ({ message, isAbort, isError }) => {
        setLiveStage(null);
        setDisplayError(undefined);
        setConversationTooLong(false);

        const toolOutput = findLatestSearchOutput(message.parts);
        if (!isAbort && !isError && toolOutput) {
          dispatch(setAILastSearch(toolOutput));
        }

        // Over-length failures are captured separately in `onError` (tagged
        // `contextLimitReached`), so they reach BQ. The remaining skipped cases
        // are user aborts and other in-stream errors — emitting those here would
        // be indistinguishable from a genuine zero-result turn.
        if (isAbort || isError) return;

        dispatch(aiPromptSentAnalytics(buildAiPromptSentPayload(message, messages, toolOutput)));
      },
      onError: (error) => {
        setLiveStage(null);
        // Record a turn that failed before any reply so BQ still receives it. All
        // these failures are pre-stream, so `onFinish` never fires for them and
        // there's no double-emit.
        const emitFailure = (status: IAIPromptSentPayload['resultsMessageStatus']) =>
          dispatch(aiPromptSentAnalytics(buildAiPromptSentPayload(null, messages, null, status)));

        // Too big to process: the gate rejects it up front (413, the common case),
        // or the model overflows mid-stream (`context_length_exceeded`).
        const overLength =
          overLengthRef.current || error.message.includes('context_length_exceeded');
        overLengthRef.current = false;
        if (overLength) {
          // Drop one more [user, assistant] pair from the middle (index 2 onward),
          // keeping the first pair as anchor; only the fetch interceptor sees the
          // trimmed slice. Guard keeps the latest user message and stops once the
          // middle is exhausted.
          const newOffset = contextStartRef.current + 2;
          if (newOffset + 2 < messages.length) {
            contextStartRef.current = newOffset;
            setContextTrimmed(true);
            void regenerate();
            return;
          }
          // Nothing left to trim (e.g. a single over-long message): show a clear,
          // actionable message instead of the generic "try again" banner.
          setConversationTooLong(true);
          emitFailure('contextLimitReached');
          return;
        }
        // 429: the rate-limit banner is driven by `rateLimit` state; just record it.
        if (rateLimitedRef.current) {
          rateLimitedRef.current = false;
          emitFailure('rateLimitReached');
        }
        setDisplayError(error);
      },
    });

  const clearRateLimit = useCallback(() => {
    clearError();
    setRateLimit(null);
  }, [clearError]);

  useRateLimitExpire(rateLimit, clearRateLimit);

  // Flag the rejected message when the rate limit flips on. The rejected message
  // is the latest user message (useChat appends it before firing the request,
  // and the input is disabled while limited). The `rateLimited` flag lives on the
  // message itself, so it survives `clearRateLimit` (the badge persists after
  // the window resets) and the transport can strip it from the payload without
  // reaching into React state.
  const wasRateLimited = useRef(false);
  useEffect(() => {
    const nowLimited = rateLimit !== null;
    if (nowLimited && !wasRateLimited.current) {
      setMessages((prev) => {
        const idx = prev.findLastIndex((m) => m.role === 'user');
        if (idx === -1 || prev[idx].metadata?.rateLimited) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          // `promptSource: 'typed'` mirrors the documented default for missing
          // metadata; a real value from `sendMessage` overrides it via the spread.
          metadata: { promptSource: 'typed', ...next[idx].metadata, rateLimited: true },
        };
        return next;
      });
    }
    wasRateLimited.current = nowLimited;
  }, [rateLimit, setMessages]);

  const dismissMessage = useCallback(
    (id: string) => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
    },
    [setMessages],
  );

  const retryMessage = useCallback(
    (message: AISearchUIMessage) => {
      const text = message.parts
        .map((p) => (p.type === 'text' ? p.text : ''))
        .join('')
        .trim();
      if (!text) return;
      // Drop the not-sent bubble (and its failed mark), then re-send the text as
      // a fresh turn. `setMessages` updates the SDK store synchronously, so the
      // `sendMessage` below already sees the pruned list — no duplicate, and the
      // retried message lands at the end of the conversation.
      dismissMessage(message.id);
      void sendMessage({ text });
    },
    [dismissMessage, sendMessage],
  );

  const clearConversation = useCallback(() => {
    if (status === 'submitted' || status === 'streaming') stop();
    setMessages([]);
    setLiveStage(null);
    setContextTrimmed(false);
    setConversationTooLong(false);
    setDisplayError(undefined);
    setRateLimit(null);
    clearError();
    contextStartRef.current = 0;
    overLengthRef.current = false;
    rateLimitedRef.current = false;
    chatIdRef.current = crypto.randomUUID();
    carouselScrollPositions.current.clear();
    dispatch(clearAIAssistantConversation());
  }, [status, stop, setMessages, clearError, dispatch]);

  const value = useMemo<AIAssistantContextValue>(
    () => ({
      messages,
      sendMessage,
      setMessages,
      stop,
      status,
      error: displayError,
      liveStage,
      promptContextReady,
      rateLimit,
      contextTrimmed,
      conversationTooLong,
      retryMessage,
      dismissMessage,
      clearConversation,
      getCarouselScroll,
      setCarouselScroll,
    }),
    [
      messages,
      sendMessage,
      setMessages,
      stop,
      status,
      displayError,
      liveStage,
      promptContextReady,
      contextTrimmed,
      conversationTooLong,
      rateLimit,
      retryMessage,
      dismissMessage,
      clearConversation,
      getCarouselScroll,
      setCarouselScroll,
    ],
  );

  return <AIAssistantCtx.Provider value={value}>{children}</AIAssistantCtx.Provider>;
};

export const useActiveAIChat = (): AIAssistantContextValue => {
  const ctx = useContext(AIAssistantCtx);
  if (!ctx) throw new Error('useActiveAIChat must be used inside AIAssistantProvider');
  return ctx;
};
